import React, { Fragment, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { Tab, Tabs, Card } from 'react-bootstrap';
import Spinner from '../layout/Spinner';
import { usePermission } from '../../hooks/usePermission';

// Existing components
import SpareParts from './SpareParts';
import SparePartPage from './SparePartPage';

// New components
import SparepartExpenses from './expenses/SparepartExpenses';
import SparepartImports from './imports/SparepartImports';

const SparePartsNew = () => {
    const { userCan } = usePermission();
    const dispatch = useDispatch();
    const location = useLocation();
    const history = useHistory();
    const [activeTab, setActiveTab] = useState('parts');
    
    const { loading } = useSelector(state => state.spare_part);

    // Handle different URL patterns
    const path = location.pathname;
    const isDetailView = /^\/spare-parts\/\d+(\/(imports|expenses|sales))?$/.test(path); // Check if it's /spare-parts/:id or /spare-parts/:id/tab

    useEffect(() => {
        // Don't set tab for detail view
        if (isDetailView) return;

        // Set active tab based on URL path
        if (path.includes('/expenses')) {
            setActiveTab('expenses');
        } else if (path.includes('/imports')) {
            setActiveTab('imports');
        } else {
            // Default to 'parts' tab for base /spare-parts route, or first available tab
            if (userCan('view spare parts')) {
                setActiveTab('parts');
            } else if (userCan('view sparepart imports')) {
                setActiveTab('imports');
                // Redirect to imports URL if user can't view parts but can view imports
                if (path === '/spare-parts') {
                    history.replace(`/spare-parts/imports${location.search}`);
                }
            } else if (userCan('view sparepart expenses')) {
                setActiveTab('expenses');
                // Redirect to expenses URL if user can't view parts but can view expenses
                if (path === '/spare-parts') {
                    history.replace(`/spare-parts/expenses${location.search}`);
                }
            }
        }
    }, [location.pathname, userCan, history, isDetailView]);

    // Handle tab change and update URL
    const handleTabChange = (tabKey) => {
        setActiveTab(tabKey);
        
        // Preserve query parameters when switching tabs
        const currentSearch = location.search;
        
        // Update URL based on selected tab
        switch (tabKey) {
            case 'parts':
                history.push(`/spare-parts${currentSearch}`);
                break;
            case 'imports':
                history.push(`/spare-parts/imports${currentSearch}`);
                break;
            case 'expenses':
                history.push(`/spare-parts/expenses${currentSearch}`);
                break;
            default:
                history.push(`/spare-parts${currentSearch}`);
        }
    };

    if (!userCan('view spare parts') && !userCan('view sparepart expenses') && !userCan('view sparepart imports')) {
        return <div className="alert alert-warning">Sizda ehtiyot qismlar bo'limiga kirish huquqi yo'q.</div>;
    }

    // Handle individual spare part detail view
    if (isDetailView) {
        if (!userCan('view spare parts')) {
            return <div className="alert alert-warning">Sizda ehtiyot qism tafsilotlarini ko'rish huquqi yo'q.</div>;
        }
        return <SparePartPage />;
    }

    return loading ? (
        <Spinner />
    ) : (
        <Fragment>
            <div className="container-fluid p-0">
                <div className="row mb-3">
                    <div className="col-md-6">
                        <h1 className="h3 mb-3">
                            <strong>Ehtiyot qismlar</strong> boshqaruvi
                        </h1>
                    </div>
                    <div className="col-md-6 text-end">
                        {/* Single import functionality removed */}
                    </div>
                </div>

                <Card>
                    <Card.Body>
                        <Tabs
                            activeKey={activeTab}
                            onSelect={handleTabChange}
                            className="mb-3"
                        >
                            {userCan('view spare parts') && (
                                <Tab eventKey="parts" title="Ehtiyot qismlar">
                                    {/* Content will be rendered conditionally below */}
                                </Tab>
                            )}

                            {userCan('view sparepart imports') && (
                                <Tab eventKey="imports" title="Import qilish">
                                    {/* Content will be rendered conditionally below */}
                                </Tab>
                            )}

                            {userCan('view sparepart expenses') && (
                                <Tab eventKey="expenses" title="Sarflar">
                                    {/* Content will be rendered conditionally below */}
                                </Tab>
                            )}
                        </Tabs>
                        
                        {/* Conditional rendering of tab content */}
                        {activeTab === 'parts' && userCan('view spare parts') && (
                            <SpareParts />
                        )}
                        
                        {activeTab === 'imports' && userCan('view sparepart imports') && (
                            <SparepartImports />
                        )}
                        
                        {activeTab === 'expenses' && userCan('view sparepart expenses') && (
                            <SparepartExpenses />
                        )}
                    </Card.Body>
                </Card>
            </div>
        </Fragment>
    );
};

export default SparePartsNew; 